import React, { useState, useEffect, useRef, useCallback } from 'react'

import {
    Box, Tooltip,
    Radio, Button, Checkbox, TextField
} from "@mui/material";
import { dateFormat, deepCopy, } from "../../../app/util";
import { toast } from 'react-toastify';
import { infoIconWhite, checkedIcon, unCheckedIcon, infoIconGrey, } from "./icons";
import PricingSeasonsMessage from './PricingSeasonsMessage';
import PricingCopyMenu from './PricingCopyMenu'
import MinInput from './MinInput';
import MaxInput from './MaxInput';
import DifferentialInput from './DifferentialInput';
import EllipsisWithTooltip from './EllipsisWithTooltip';
import { useSelector } from "react-redux";
import { formatSeasonDateRange } from './function';
import moment from 'moment';
import 'overlayscrollbars/overlayscrollbars.css';
import { OverlayScrollbarsComponent, useOverlayScrollbars } from "overlayscrollbars-react";

function RoomPricingTable({ seasonEditCopyRef, season, manageSeasonChangeFunc, isReadOnlyMode, isNew, inventoryRoomCodes, inventoryRoomSettings }) {
    const { profile } = useSelector(state => state.auth);
    const { display_name } = profile;
    const {
        plan_name,
        base_room,
        dependent_room_type,
        isDefault,
        status,
        plan_period,
        id,
        last_status_edited_by,
        last_status_edited_on,
        statusHistory
    } = seasonEditCopyRef.current;

    const { ui_state } = useSelector((state) => state.app.current_state);

    const { room_type_differential, room_type } = dependent_room_type || { room_type: {}, room_type_differential: 'absolute' };
    const { currency_symbol } = ui_state;
    const pricingSeasonsUI = deepCopy(ui_state.pricingSeasonsUI);
    const { tabPanelIndex, changeItemId, discardCount, globalErrorMsg, copyInputCount } = pricingSeasonsUI;

    const offsetType = seasonEditCopyRef?.current.dependent_room_type?.room_type_differential || room_type_differential;
    const dependentRooms = inventoryRoomCodes; //Object.keys(room_type);

    const getRoomTypeDisplayName = useCallback((roomCode) => {
        const { display_name } = inventoryRoomSettings.find(item => item.room_lodgiq_code === roomCode) || {};
        return display_name || roomCode;
    });

    const [showOffsetSwitchingLoader, setShowOffsetSwitchingLoader] = useState(false);
    const offsetSwitchingLoader = (
        <Box
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                inset: 0,// fills parent container
                backgroundColor: "transparent",
                //backdropFilter: "blur(1px)", // backdrop blur
                pointerEvents: "all", // intercept mouse actions
                cursor: "default",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
            }
            }
        >
            <Box className="offset-switching-loader" >
                Switching to {offsetType === "percentage" ? "Percent" : "Absolute"} Offset
            </Box>
        </Box>
    )

    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const [showMessage, setShowMessage] = useState(false);
    const [activePricingSeason, setActivePricingSeason] = useState(status === "active" ? false : true);


    const handleSaveBtn = (changeSeason, activePricingSeason, season) => {
        //console.log("changeSeason",changeSeason)
        changeSeason.id = changeSeason.id.replace("new_", ""); // remove new_ from id
        changeSeason.last_edited_by = display_name;
        changeSeason.last_edited_on = moment().utc().format('YYYY-MM-DD HH:mm:ss');
        changeSeason.last_status_edited_by = display_name;
        changeSeason.last_status_edited_on = moment().utc().format('YYYY-MM-DD HH:mm:ss');
        changeSeason.isRoomTypeDifference = false;
        changeSeason.statusHistory = 'active';
        changeSeason.isBaseRoomTypeChanged = false;
        manageSeasonChangeFunc({ action: 'saveBtn', season: changeSeason });
    }

    const handleCancelBtn = () => {
        manageSeasonChangeFunc({ action: 'cancelBtn', season: season });
    }

    const handleUpdateBtn = (changeSeason, activePricingSeason, season) => {
        const initialStatus = season.status;
        const changeSeasonStatus = changeSeason.status;
        changeSeason.last_edited_by = display_name;
        changeSeason.last_edited_on = moment().utc().format('YYYY-MM-DD HH:mm:ss');
        changeSeason.isRoomTypeDifference = false;
        changeSeason.statusHistory = changeSeasonStatus;
        changeSeason.isBaseRoomTypeChanged = false;

        // check status change
        if (initialStatus === changeSeasonStatus) {
            manageSeasonChangeFunc({ action: 'updateBtn', season: changeSeason });
        }
        else {
            if (tabPanelIndex === 0) {
                setShowMessage(true);
            } else {
                changeSeason.last_status_edited_by = display_name;
                changeSeason.last_status_edited_on = moment().utc().format('YYYY-MM-DD HH:mm:ss');
                manageSeasonChangeFunc({ action: 'updateStatus', season: changeSeason });
            }
            // showActiveInactiveToast();
        }

    }

    const handleDiscardBtn = (season, id) => {
        seasonEditCopyRef.current = deepCopy(season);
        manageSeasonChangeFunc({ action: 'discardBtn', season: season });
    }

    const handleClickOkayButton = (changeSeason) => {
        setShowMessage(false);
        manageSeasonChangeFunc({ action: 'updateStatus', season: changeSeason });
        showActiveInactiveToast();
    };

    const showActiveInactiveToast = () => {

        if (activePricingSeason) {
            toast.success(plan_name + " has been deactivated.", {
                position: "bottom-right",
                autoClose: 3000,
            });
        } else {
            toast.success(plan_name + " has been activated.", {
                position: "bottom-right",
                autoClose: 3000,
            });
        }
    };

    const renderButton = ({ label, onClick, active = false, btnClass }) => {
        if (tabPanelIndex === 2) {
            return null;
        }
        return (
            <Tooltip
                arrow
                title={label.trim() === "Save" || label.trim() === "Update" ? globalErrorMsg.at(-1) : ''}
                placement="top"
                slotProps={{
                    tooltip: {
                        sx: {
                            maxWidth: 180,
                        },
                    },
                }}
            >
                <span>
                    <Button
                        disabled={active ? false : true}
                        className={`${btnClass} ${active ? 'btn-active' : ''}`}
                        onClick={active ? onClick : null}
                    >
                        {label}
                    </Button>
                </span>
            </Tooltip>
        );
    }

    const handleDeactivateCheckbox = (e) => {
        if (tabPanelIndex === 0) {
            seasonEditCopyRef.current.status = e.target.checked ? "inactive" : "active";
            setActivePricingSeason(e.target.checked);
        } else {
            seasonEditCopyRef.current.status = e.target.checked ? "active" : "inactive";
            setActivePricingSeason(!e.target.checked);
        }
        manageSeasonChangeFunc({ action: 'deactivateCheckbox', season: seasonEditCopyRef.current });
    }

    useEffect(() => {
        const activePricingSeason = season.status === "active" ? false : true;
        setActivePricingSeason(activePricingSeason);
    }, [discardCount])

    const handleOffsetTypeChange = (type) => {
        // Show offset switching loader
        setShowOffsetSwitchingLoader(true);

        // Update offset type
        seasonEditCopyRef.current.dependent_room_type.room_type_differential = type;

        // Clear all dependent room input values
        const roomTypeData = seasonEditCopyRef.current.dependent_room_type.room_type;
        Object.keys(roomTypeData).forEach(room => {
            Object.keys(roomTypeData[room]).forEach(day => {
                roomTypeData[room][day] = null; // empty all values
            });
        });

        // Re-render the updated changes
        manageSeasonChangeFunc({
            action: 'offsetTypeChange',
            season: seasonEditCopyRef.current
        });

        // Stop offset switching loader after delay
        setTimeout(() => {
            setShowOffsetSwitchingLoader(false);
            console.log("seasonEditCopyRef.current", seasonEditCopyRef.current);
        }, 2000);
    };

    const inputRefs = {
        min: {},
        max: {},
    };

    const registerRef = (type, day, refObj) => {
        inputRefs[type][day] = refObj;   // store ref OBJECT, not ref.current
    };

    const onNavigate = (direction, day, type) => {
        const days = Object.keys(inputRefs.min);
        const index = days.indexOf(day);

        if (direction === "left" && index > 0) {
            const prev = days[index - 1];
            inputRefs[type][prev]?.current?.focus();
            return;
        }

        if (direction === "right" && index < days.length - 1) {
            const next = days[index + 1];
            inputRefs[type][next]?.current?.focus();
            return;
        }

        if (direction === "up" && type === "max") {
            inputRefs.min[day]?.current?.focus();
            return;
        }

        if (direction === "down" && type === "min") {
            inputRefs.max[day]?.current?.focus();
            return;
        }
    };

    return (
        <Box className="accordion-details-container" >
            {/* Base Room Type */}
            <Box className="subtitle-base-room-type"> Base Room Type </Box>

            <Box className="room-grid base-room">
                <Box className="room-header">
                    <Box className="grid-col">Room Type</Box>
                    <Box className="grid-col">Rate</Box>
                    {days.map(day => (
                        <Box className="grid-col" key={day}>
                            {day} ({currency_symbol})
                        </Box>
                    ))}
                </Box>

                <Box className="room-body">
                    <Box className="left-section" >
                        <Box className="grid-col" >
                            <EllipsisWithTooltip text={base_room} />
                        </Box>
                    </Box>
                    <Box className="right-section">
                        <Box className={`grid-row ${isReadOnlyMode ? 'grid-row-read-only' : ''}`}>
                            <Box className="grid-col celing">Ceiling</Box>
                            {days.map((day, i) => (
                                <MaxInput
                                    key={`max_${day}`}
                                    isReadOnlyMode={isReadOnlyMode}
                                    day={day}
                                    season={season}
                                    seasonEditCopyRef={seasonEditCopyRef}
                                    manageSeasonChangeFunc={manageSeasonChangeFunc}
                                    discardCount={discardCount}
                                    globalErrorMsg={globalErrorMsg}
                                    disableDownCopy={true}
                                    disableRightCopy={i === days.length - 1}
                                    copyInputCount={copyInputCount}
                                    registerRef={registerRef}
                                    onNavigate={onNavigate}
                                />
                            ))}
                        </Box>
                        <Box className={`grid-row ${isReadOnlyMode ? 'grid-row-read-only' : ''}`}>
                            <Box className="grid-col floor">Floor</Box>
                            {days.map((day, i) => (
                                <MinInput
                                    key={`min_${day}`}
                                    isReadOnlyMode={isReadOnlyMode}
                                    day={day}
                                    season={season}
                                    seasonEditCopyRef={seasonEditCopyRef}
                                    manageSeasonChangeFunc={manageSeasonChangeFunc}
                                    discardCount={discardCount}
                                    globalErrorMsg={globalErrorMsg}
                                    disableDownCopy={true}
                                    disableRightCopy={i === days.length - 1}
                                    copyInputCount={copyInputCount}
                                    registerRef={registerRef}
                                    onNavigate={onNavigate}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Dependent Room Type Differential */}
            <Box className="subtitle-dependent-room-type" >
                <Box className="left" >
                    <Box className="subtitle" > Dependent Room Type Differential{!isReadOnlyMode && ":"} </Box>
                    {
                        !isReadOnlyMode && (<>
                            <Box className="radio-btn" >
                                <Radio
                                    size="small"
                                    className="colored-checkbox"
                                    name="differential_offset"
                                    checked={offsetType === "absolute"
                                    }
                                    onChange={() => handleOffsetTypeChange("absolute")}
                                />
                                < div className={`option-label ${offsetType === "absolute" ? 'active' : ''}`} onClick={() => handleOffsetTypeChange("absolute")}> {currency_symbol} Offset </div>
                            </Box>
                            <Box className="radio-btn" >
                                <Radio
                                    size="small"
                                    className="colored-checkbox"
                                    name="differential_offset"
                                    checked={offsetType === "percentage"}
                                    onChange={() => handleOffsetTypeChange("percentage")}
                                />
                                <div className={`option-label ${offsetType === "percentage" ? 'active' : ''}`} onClick={() => handleOffsetTypeChange("percentage")}> Percent offset </div>
                            </Box>
                        </>)}

                </Box>
                <Box className="right" >
                    {!isReadOnlyMode && (<>
                        {infoIconGrey}
                        <div> You can choose either a currency-based or a percent-based differential.</div>
                    </>)}
                </Box>
            </Box>

            <Box className="room-grid dependent-room" sx={isReadOnlyMode ? { marginBottom: "0px !important" } : { position: "relative" }}>
                {/* Offset switching loader */}
                {showOffsetSwitchingLoader ? offsetSwitchingLoader : null}

                <Box className="room-header">
                    <Box className="grid-col">Room Type </Box>
                    {
                        days.map(day => (
                            <Box className="grid-col" key={day} >
                                {day} {offsetType === "absolute" && `(${currency_symbol})`}
                            </Box>
                        ))}
                </Box>

                <OverlayScrollbarsComponent
                    className="custom-overlay-scrollbar scrollable-body-container"
                    element="div"
                    options={{
                        scrollbars: { autoHide: 'leave', autoHideDelay: 100 },
                        overflow: { x: 'hidden', y: 'scroll', }
                    }}
                    defer
                >
                    <Box className="room-body-container">
                        {dependentRooms.map((room, index) => (
                            <Box className="room-body">
                                <Box className="left-section">
                                    <Box className="grid-col">
                                        <EllipsisWithTooltip text={getRoomTypeDisplayName(room)} />
                                    </Box>
                                </Box>
                                <Box className="right-section">
                                    <Box className={`grid-row ${isReadOnlyMode ? 'grid-row-read-only' : ''}`}>
                                        {days.map((day, i) => (
                                            <DifferentialInput
                                                key={`differential_${day}`}
                                                isReadOnlyMode={isReadOnlyMode}
                                                day={day}
                                                room={room}
                                                season={season}
                                                seasonEditCopyRef={seasonEditCopyRef}
                                                manageSeasonChangeFunc={manageSeasonChangeFunc}
                                                discardCount={discardCount}
                                                offsetType={offsetType}
                                                disableDownCopy={index === dependentRooms.length - 1}
                                                disableRightCopy={i === days.length - 1}
                                                copyInputCount={copyInputCount}
                                                inventoryRoomCodes={inventoryRoomCodes}
                                            />
                                        ))}
                                    </Box>
                                    {/* Grid row can be added for model 3 design */}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </OverlayScrollbarsComponent>
            </Box>

            {/* Action Buttons */}
            {
                // !isReadOnlyMode && (<>
                true && (<>
                    <Box className="accordion-action-buttons" >
                        <label className="deactivate-switch" >
                            {!isDefault && !isNew && (
                                <>
                                    {tabPanelIndex === 2 && (
                                        <>
                                            <Box>
                                                {last_status_edited_by && last_status_edited_on ? (
                                                    <span className="status_edited">
                                                        {statusHistory === 'active' ? 'Activated' : 'Deactivated'} by {last_status_edited_by} |{' '}
                                                        {dateFormat(last_status_edited_on, 'ddd, MMM DD, YYYY')}
                                                    </span>
                                                ) : null}
                                            </Box>
                                        </>
                                    )}
                                    {
                                        tabPanelIndex === 1 &&
                                        <>
                                            <Checkbox
                                                checked={!activePricingSeason}
                                                onClick={(e) => { handleDeactivateCheckbox(e) }}
                                                icon={unCheckedIcon}
                                                checkedIcon={checkedIcon}
                                            />
                                            <Box className={`checkbox-label ${!activePricingSeason ? 'active' : ''}`}>
                                                Activate
                                                {
                                                    last_status_edited_by && last_status_edited_on ?
                                                        <span className='status_edited' > (Deactivated by {last_status_edited_by} | {dateFormat(last_status_edited_on, "ddd, MMM DD, YYYY")})</span>
                                                        :
                                                        null
                                                }
                                            </Box>
                                        </>
                                    }

                                    {
                                        tabPanelIndex === 0 &&
                                        <>
                                            <Checkbox
                                                checked={activePricingSeason}
                                                onClick={(e) => { handleDeactivateCheckbox(e) }
                                                }
                                                icon={unCheckedIcon}
                                                checkedIcon={checkedIcon}
                                            />
                                            <Box className={`checkbox-label ${activePricingSeason ? 'active' : ''}`}>Deactivate </Box>
                                        </>
                                    }

                                    {/* {tabPanelIndex === 0 ?
                                        <>
                                            <Checkbox
                                                checked={activePricingSeason}
                                                onClick={ (e) => { handleDeactivateCheckbox(e) } }
                                                icon={unCheckedIcon}
                                                checkedIcon={checkedIcon}
                                            />
                                            <Box>Deactivate</Box>
                                        </> 
                                        : 
                                        <>
                                            <Checkbox
                                                checked={!activePricingSeason}
                                                onClick={ (e) => { handleDeactivateCheckbox(e) } }
                                                icon={unCheckedIcon}
                                                checkedIcon={checkedIcon}
                                            />
                                            <Box>
                                                Activate {last_status_edited_by && last_status_edited_on ?
                                                    <span className='status_edited'>(Deactivated by {last_status_edited_by} | {dateFormat(last_status_edited_on, "ddd, MMM DD, YYYY")})</span>
                                                    : 
                                                    null
                                                }
                                            </Box>

                                        </>
                                    } */}
                                </>
                            )}
                        </label>
                        <Box className="save-cancel-buttons" >
                            {
                                renderButton(
                                    {
                                        label: `${isNew ? 'Cancel' : 'Discard Changes'}`,
                                        onClick: () => {
                                            if (isNew) {
                                                handleCancelBtn(seasonEditCopyRef.current)
                                            } else {
                                                handleDiscardBtn(season, null)
                                            }
                                        },
                                        active: isNew ? true : changeItemId === id,
                                        btnClass: 'navigate-cancel-button'
                                    }
                                )}
                            {renderButton(
                                {
                                    label: `${isNew ? 'Save' : 'Update'}`,
                                    onClick: () => {
                                        if (isNew) {
                                            handleSaveBtn(seasonEditCopyRef.current, activePricingSeason, season)
                                        } else {
                                            handleUpdateBtn(seasonEditCopyRef.current, activePricingSeason, season)
                                        }
                                    },
                                    active: changeItemId === id && globalErrorMsg.length === 0,
                                    btnClass: 'navigate-save-button'
                                }
                            )}
                        </Box>
                    </Box>
                    <PricingSeasonsMessage svgIcon={infoIconWhite} message={
                        <>
                            <span className='semi-bold'>Missing Pricing Season for  {formatSeasonDateRange(
                                plan_period.effective_date_range.from,
                                plan_period.effective_date_range.until
                            )}.
                            </span> Set up a Season for This Date Range, Otherwise the Default Season Will Guide Price Recommendations.
                        </>
                    } shoWMessage={showMessage} okayButtonOnClick={() => handleClickOkayButton(seasonEditCopyRef.current)} />
                </>)}
        </Box>
    );
}

export default RoomPricingTable
